import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
// Sem as extensoes de tabela o TipTap DESCARTA <table> ao parsear o HTML da
// materia; o getHTML() seguinte devolve o texto sem a tabela e o save grava
// isso por cima do artigo publicado. Medido em producao (27/08): a tabela de
// cotacoes da B3 aparecia achatada no editor — abrir e salvar apagaria a
// tabela da materia no ar. Nao e estetica, e perda de dado.
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect } from 'react';

import styles from './RichTextEditor.module.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

function Toolbar({ editor }: { editor: Editor }) {
  return (
    <div className={styles.toolbar}>
      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        label="B"
        title="Negrito (Ctrl+B)"
        style={{ fontWeight: 700 }}
      />
      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        label="I"
        title="Itálico (Ctrl+I)"
        style={{ fontStyle: 'italic' }}
      />
      <ToolbarButton
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        label="S"
        title="Tachado"
        style={{ textDecoration: 'line-through' }}
      />
      <Separator />
      <ToolbarButton
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        label="H2"
        title="Cabeçalho 2"
      />
      <ToolbarButton
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        label="H3"
        title="Cabeçalho 3"
      />
      <Separator />
      <ToolbarButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        label="•"
        title="Lista"
      />
      <ToolbarButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        label="1."
        title="Lista numerada"
      />
      <ToolbarButton
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        label="❝"
        title="Citação"
      />
      <Separator />
      <ToolbarButton
        active={editor.isActive('link')}
        onClick={() => {
          const previousUrl = editor.getAttributes('link').href as string | undefined;
          const url = window.prompt('URL do link:', previousUrl ?? '');
          if (url === null) return;
          if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
          }
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }}
        label="🔗"
        title="Link"
      />
      <Separator />
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        label="↶"
        title="Desfazer"
        disabled={!editor.can().undo()}
      />
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        label="↷"
        title="Refazer"
        disabled={!editor.can().redo()}
      />
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  style?: React.CSSProperties;
}

function ToolbarButton({ label, onClick, active, disabled, title, style }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${styles.tbButton} ${active ? styles.tbButtonActive : ''}`}
      title={title}
      style={style}
    >
      {label}
    </button>
  );
}

function Separator() {
  return <div className={styles.separator} />;
}

export function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? 'Comece a escrever…' }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  return (
    <div className={styles.root}>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} className={styles.content} />
    </div>
  );
}
